import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiShield,
  FiAlertTriangle,
  FiClock,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiFilter,
  FiRefreshCw,
  FiLock,
  FiFlag,
  FiActivity,
  FiInbox,
} from "react-icons/fi";
import communityService from "../../../services/communityService";
import ReportCard from "./ReportCard";
import ModActionLogItem from "./ModActionLogItem";
import "../styles/index.css";
import "./collab-moderation.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const fadeVariants = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.25 },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: { duration: 0.2 },
  },
};

const ModerationDashboard = ({ communityId, userRole }) => {
  const [activeTab, setActiveTab] = useState("reports");
  const [reports, setReports] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [reportStatus, setReportStatus] = useState("pending");
  const [refreshing, setRefreshing] = useState(false);

  // Pagination state
  const [actionsPage, setActionsPage] = useState(1);
  const [hasMoreActions, setHasMoreActions] = useState(false);

  // Permission check
  const canModerate = userRole === "creator" || userRole === "moderator";

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await communityService.getModerationReports(
        communityId,
        reportStatus
      );
      if (response.success) {
        setReports(response.data.reports || []);
      }
    } catch (error) {
toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const loadActions = async () => {
    try {
      setLoading(true);
      const response = await communityService.getModerationActions(
        communityId,
        actionsPage,
        20
      );
      if (response.success) {
        setActions(response.data?.actions || response.actions || []);
        const pagination = response.data?.pagination || response.pagination;
        setHasMoreActions(pagination?.hasNext || false);
      }
    } catch (error) {
toast.error("Failed to load moderation actions log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canModerate && communityId) {
      if (activeTab === "reports") {
        loadReports();
      } else {
        loadActions();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId, activeTab, reportStatus, actionsPage, canModerate]);

  const handleRefresh = async () => {
    setRefreshing(true);
    if (activeTab === "reports") {
      await loadReports();
    } else {
      await loadActions();
    }
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const handleReportAction = async (reportId, action, moderatorNotes) => {
    try {
      setActionLoading(true);
      
      let response;
      if (action === "dismiss") {
        // Dismiss uses a separate endpoint
        response = await communityService.dismissModerationReport(
          communityId,
          reportId,
          moderatorNotes // moderatorNotes becomes the "reason" for dismiss
        );
      } else {
        // All other actions (content_removed, user_warned, user_muted, user_banned, none)
        response = await communityService.actionModerationReport(
          communityId,
          reportId,
          action,
          moderatorNotes
        );
      }

      // Check for success - handle both response.success and response.data.success structures
      const isSuccess = response.success || response.data?.success !== false;
      
      if (isSuccess) {
        const actionLabel = action === "dismiss" ? "dismissed" : 
                           action === "content_removed" ? "removed" :
                           action === "none" ? "approved" : action;
        toast.success(`Report ${actionLabel} successfully`);
        
        // Always remove from local state after successful action
        setReports((prev) => prev.filter((r) => r._id !== reportId));
        
        // Refresh actions log if on that tab
        if (activeTab === "actions") {
          loadActions();
        }
      } else {
        toast.error("Action completed but status unclear - please refresh");
      }
    } catch (error) {
toast.error(error.message || "Failed to process report action");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <FiClock />;
      case "reviewing":
        return <FiActivity />;
      case "actioned":
        return <FiCheckCircle />;
      case "dismissed":
        return <FiFilter />;
      default:
        return <FiFlag />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "amber";
      case "reviewing":
        return "blue";
      case "actioned":
        return "green";
      case "dismissed":
        return "slate";
      default:
        return "primary";
    }
  };

  // No permission view
  if (!canModerate) {
    return (
      <motion.div
        className="collab-comm-moderation"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="collab-comm-mod-no-permission">
          <motion.div
            className="collab-comm-mod-no-permission-icon"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <FiLock size={40} />
          </motion.div>
          <h3>Access Restricted</h3>
          <p>
            You must be a moderator or community creator to access the
            moderation dashboard.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="collab-comm-moderation"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header */}
      <motion.div className="collab-comm-mod-header" variants={itemVariants}>
        <div className="collab-comm-mod-header-left">
          <div className="collab-comm-mod-header-icon">
            <FiShield size={24} />
          </div>
          <div className="collab-comm-mod-header-info">
            <h2>Moderation Dashboard</h2>
            <p>Review reports and manage community content</p>
          </div>
        </div>
        <button
          className="collab-comm-mod-refresh-btn"
          onClick={handleRefresh}
          disabled={refreshing || loading}
        >
          <FiRefreshCw
            size={16}
            className={refreshing ? "collab-comm-spinning" : ""}
          />
          Refresh
        </button>
      </motion.div>

      {/* Tabs */}
      <motion.div className="collab-comm-mod-tabs" variants={itemVariants}>
        <button
          className={`collab-comm-mod-tab ${
            activeTab === "reports" ? "active" : ""
          }`}
          onClick={() => setActiveTab("reports")}
        >
          <FiFlag size={18} />
          <span>Reports</span>
          {reports.length > 0 && reportStatus === "pending" && (
            <span className="collab-comm-mod-tab-badge">{reports.length}</span>
          )}
        </button>
        <button
          className={`collab-comm-mod-tab ${
            activeTab === "actions" ? "active" : ""
          }`}
          onClick={() => setActiveTab("actions")}
        >
          <FiActivity size={18} />
          <span>Mod Actions Log</span>
        </button>
      </motion.div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === "reports" && (
          <motion.div
            key="reports"
            className="collab-comm-mod-content"
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Filters */}
            <div className="collab-comm-mod-filters">
              <div className="collab-comm-mod-filter-group">
                <FiFilter size={16} />
                <label>Status:</label>
                <div className="collab-comm-mod-status-pills">
                  {["pending", "reviewing", "actioned", "dismissed"].map(
                    (status) => (
                      <button
                        key={status}
                        className={`collab-comm-mod-status-pill ${status} ${
                          reportStatus === status ? "active" : ""
                        }`}
                        onClick={() => setReportStatus(status)}
                      >
                        {getStatusIcon(status)}
                        <span>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </span>
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>

            {/* Reports List */}
            {loading ? (
              <div className="collab-comm-mod-loading">
                <div className="collab-comm-loading-spinner" />
                <p>Loading reports...</p>
              </div>
            ) : reports.length === 0 ? (
              <motion.div
                className="collab-comm-mod-empty"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div
                  className={`collab-comm-mod-empty-icon ${getStatusColor(
                    reportStatus
                  )}`}
                >
                  {reportStatus === "pending" ? (
                    <FiInbox size={36} />
                  ) : (
                    <FiCheckCircle size={36} />
                  )}
                </div>
                <h3>No {reportStatus} reports</h3>
                <p>
                  {reportStatus === "pending"
                    ? "All caught up! There are no pending reports to review."
                    : `No ${reportStatus} reports found in the system.`}
                </p>
              </motion.div>
            ) : (
              <motion.div
                className="collab-comm-mod-reports-list"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {reports.map((report) => (
                  <ReportCard
                    key={report._id}
                    report={report}
                    onAction={handleReportAction}
                    loading={actionLoading}
                  />
                ))}
              </motion.div>
            )}
          </motion.div>
        )}

        {activeTab === "actions" && (
          <motion.div
            key="actions"
            className="collab-comm-mod-content"
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {loading ? (
              <div className="collab-comm-mod-loading">
                <div className="collab-comm-loading-spinner" />
                <p>Loading moderation actions...</p>
              </div>
            ) : actions.length === 0 ? (
              <motion.div
                className="collab-comm-mod-empty"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="collab-comm-mod-empty-icon slate">
                  <FiActivity size={36} />
                </div>
                <h3>No moderation actions</h3>
                <p>
                  Moderation actions will appear here once you start moderating
                  content.
                </p>
              </motion.div>
            ) : (
              <>
                <motion.div
                  className="collab-comm-mod-actions-list"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {actions.map((action) => (
                    <ModActionLogItem key={action._id} action={action} />
                  ))}
                </motion.div>

                {(actionsPage > 1 || hasMoreActions) && (
                  <div className="collab-comm-mod-pagination">
                    <button
                      onClick={() => setActionsPage((p) => Math.max(1, p - 1))}
                      disabled={actionsPage === 1}
                      className="collab-comm-mod-pagination-btn"
                    >
                      <FiChevronLeft size={18} />
                      Previous
                    </button>
                    <span className="collab-comm-mod-pagination-info">
                      Page {actionsPage}
                    </span>
                    <button
                      onClick={() => setActionsPage((p) => p + 1)}
                      disabled={!hasMoreActions}
                      className="collab-comm-mod-pagination-btn"
                    >
                      Next
                      <FiChevronRight size={18} />
                    </button>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ModerationDashboard;
